import loadable from '@loadable/component';
import { Box, CircularProgress } from '@mui/material';
import { AnoynmousLayout } from './features/shared/components/layouts/AnonymousLayout';
import { AuthenticatedLayout } from './features/shared/components/layouts/AuthenticatedLayout';
import { ProtectedRoute } from './features/shared/components/ProtectedRoute';
import { RequireOrderIntent } from './features/shared/components/RequireOrderIntent';
import { ORGANIZATIONS_ACCESS_ROLES, USERS_ACCESS_ROLES, USER_GROUP_ACCESS_ROLES, PURCHASE_HISTORY_ACCESS_ROLES } from './features/shared/constants';
import { useLocation } from 'react-router-dom';

// Shown by every lazy-loaded route while its JS chunk is being fetched, so navigating to
// a not-yet-loaded page shows a spinner instead of a blank gap.
const RouteLoadingFallback = (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <CircularProgress size={36} thickness={4} sx={{ color: '#0891B2' }} />
    </Box>
);

const SignInPage = loadable(() => import('./features/auth/pages/SignIn'), { resolveComponent: components => components.SignIn, fallback: RouteLoadingFallback });
export const SignInRoute = () => (
    <AnoynmousLayout>
        <SignInPage />
    </AnoynmousLayout>
);

const SignInSimplyPerioPage = loadable(() => import('./features/guidance/perio/pages/SignIn'), { resolveComponent: components => components.SignInSimplyPerio, fallback: RouteLoadingFallback });
export const SignInSimplyPerioRoute = () => (
    <AnoynmousLayout>
        <SignInSimplyPerioPage />
    </AnoynmousLayout>
);

const SignInSimplyImplantitisPage = loadable(() => import('./features/guidance/implantitis/pages/SignIn'), { resolveComponent: components => components.SignInSimplyImplantitis, fallback: RouteLoadingFallback });
export const SignInSimplyImplantitisRoute = () => (
    <AnoynmousLayout>
        <SignInSimplyImplantitisPage />
    </AnoynmousLayout>
);

const ResetPasswordPage = loadable(() => import('./features/auth/pages/ResetPassword'), { resolveComponent: components => components.ResetPassword, fallback: RouteLoadingFallback });
export const ResetPasswordRoute = () => (
    <AnoynmousLayout>
        <ResetPasswordPage />
    </AnoynmousLayout>
)

const ResetPasswordChangePage = loadable(() => import('./features/auth/pages/ResetPasswordChange'), { resolveComponent: components => components.ResetPasswordChange, fallback: RouteLoadingFallback });
export const ResetPasswordChangeRoute = () => (
    <AnoynmousLayout>
        <ResetPasswordChangePage />
    </AnoynmousLayout>
)

const DashboardPage = loadable(() => import('./features/dashboard/pages/DashboardLanding'), { resolveComponent: components => components.DashboardLanding, fallback: RouteLoadingFallback });
export const DashboardRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <DashboardPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const DocDisplay = loadable(() => import('./features/guidance/perio/pages/DocDisplay'), { resolveComponent: components => components.DocDisplay, fallback: RouteLoadingFallback });
export const DocDisplayRoute = () => (
    <ProtectedRoute>
        <AnoynmousLayout>
            <DocDisplay />
        </AnoynmousLayout>
    </ProtectedRoute>
);

const DocDisplayImplantitis = loadable(() => import('./features/guidance/implantitis/pages/DocDisplay'), { resolveComponent: components => components.DocDisplayImplantitis, fallback: RouteLoadingFallback });
export const DocDisplayImplantitisRoute = () => (
    <ProtectedRoute>
        <AnoynmousLayout>
            <DocDisplayImplantitis />
        </AnoynmousLayout>
    </ProtectedRoute>
);

const PrefilterOrdersPage = loadable(() => import('./features/orders/search/pages/PrefilterOrdersSearch'), { resolveComponent: components => components.PrefilterOrdersSearch, fallback: RouteLoadingFallback });
export const PrefilterOrdersRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <PrefilterOrdersPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const NewReportOrdersPage = loadable(() => import('./features/orders/search/pages/NewReportOrdersSearch'), { resolveComponent: components => components.NewReportOrdersSearch, fallback: RouteLoadingFallback });
export const NewReportOrdersRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <NewReportOrdersPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const OrdersPage = loadable(() => import('./features/orders/search/pages/OrdersSearch'), { resolveComponent: components => components.OrdersSearch, fallback: RouteLoadingFallback });
export const OrdersRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrdersPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

// Split out of the Orders page (used to be a "Kit Orders" tab there) into its own route/nav entry.
const KitOrdersPage = loadable(() => import('./features/orders/search/pages/KitOrdersSearch'), { resolveComponent: components => components.KitOrdersSearch, fallback: RouteLoadingFallback });
export const KitOrdersRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <RequireOrderIntent>
                <KitOrdersPage />
            </RequireOrderIntent>
        </AuthenticatedLayout>
    </ProtectedRoute>
);

// Sep 2026 - Purchase History. Role-gated at the route as well as in the nav, so the page cannot
// be reached by typing the URL; the server enforces the same set independently via
// JwtAppUser.CanViewPurchaseHistory, which is what actually protects the data.
const PurchaseHistoryPage = loadable(() => import('./features/purchaseHistory/pages/PurchaseHistory'), { resolveComponent: components => components.PurchaseHistory, fallback: RouteLoadingFallback });
export const PurchaseHistoryRoute = () => (
    <ProtectedRoute roles={PURCHASE_HISTORY_ACCESS_ROLES}>
        <AuthenticatedLayout>
            <PurchaseHistoryPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const OrdersCreatePage = loadable(() => import ('./features/orders/add-edit/pages/CreateOrder'), { resolveComponent: components => components.CreateOrder, fallback: RouteLoadingFallback });
export const OrdersCreateRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <RequireOrderIntent requireTrueAdmin>
                <OrdersCreatePage />
            </RequireOrderIntent>
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const OrdersEditPage = loadable(() => import ('./features/orders/add-edit/pages/EditOrder'), { resolveComponent: components => components.EditOrder, fallback: RouteLoadingFallback });
export const OrdersEditRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrdersEditPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const UserGroupsPage = loadable(() => import('./features/userGroups/search/pages/UserGroupsSearch'), { resolveComponent: components => components.UserGroupsSearch, fallback: RouteLoadingFallback });
export const UserGroupsRoute = () => (
    <ProtectedRoute roles={USER_GROUP_ACCESS_ROLES}>
        <AuthenticatedLayout>
            <UserGroupsPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const UserGroupsCreatePage = loadable(() => import('./features/userGroups/add-edit/pages/UserGroupsCreate'), { resolveComponent: components => components.UserGroupsCreate, fallback: RouteLoadingFallback });
export const UserGroupsCreateRoute = () => (
    <ProtectedRoute roles={USER_GROUP_ACCESS_ROLES}>
        <AuthenticatedLayout>
            <UserGroupsCreatePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const UserGroupsEditPage = loadable(() => import('./features/userGroups/add-edit/pages/UserGroupsEdit'), { resolveComponent: components => components.UserGroupsEdit, fallback: RouteLoadingFallback });
export const UserGroupsEditRoute = () => (
    <ProtectedRoute roles={USER_GROUP_ACCESS_ROLES}>
        <AuthenticatedLayout>
            <UserGroupsEditPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const UsersPage = loadable(() => import('./features/users/search/pages/UsersSearch'), { resolveComponent: components => components.UsersSearch, fallback: RouteLoadingFallback });
export const UsersRoute = () => (
    <ProtectedRoute roles={[...USERS_ACCESS_ROLES, ...ORGANIZATIONS_ACCESS_ROLES, ...USER_GROUP_ACCESS_ROLES]}>
        <AuthenticatedLayout>
            <UsersPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const UsersCreatePage = loadable(() => import('./features/users/add-edit/pages/CreateUser'), { resolveComponent: components => components.CreateUser, fallback: RouteLoadingFallback });
export const UsersCreateRoute = () => (
    <ProtectedRoute roles={[...USERS_ACCESS_ROLES, ...ORGANIZATIONS_ACCESS_ROLES, ...USER_GROUP_ACCESS_ROLES]}>
        <AuthenticatedLayout>
            <UsersCreatePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);


const UsersEditPage = loadable(() => import('./features/users/add-edit/pages/EditUser'), { resolveComponent: components => components.EditUser, fallback: RouteLoadingFallback });
export const UsersEditRoute = () => (
    <ProtectedRoute roles={[...USERS_ACCESS_ROLES, ...ORGANIZATIONS_ACCESS_ROLES, ...USER_GROUP_ACCESS_ROLES]}>
        <AuthenticatedLayout>
            <UsersEditPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const OrganizationsPage = loadable(() => import('./features/organizations/search/pages/OrganizationSearch'), { resolveComponent: components => components.OrganizationSearch, fallback: RouteLoadingFallback });
export const OrganizationsRoute = () => (
    <ProtectedRoute roles={[...ORGANIZATIONS_ACCESS_ROLES, ...USER_GROUP_ACCESS_ROLES]}>
        <AuthenticatedLayout>
            <OrganizationsPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const OrganizationsCreatePage = loadable(() => import('./features/organizations/add-edit/pages/CreateOrganization'), { resolveComponent: components => components.CreateOrganization, fallback: RouteLoadingFallback });
export const OrganizationsCreateRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrganizationsCreatePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const OrganizationsEditPage = loadable(() => import('./features/organizations/add-edit/pages/EditOrganization'), { resolveComponent: components => components.EditOrganization, fallback: RouteLoadingFallback });
export const OrganizationsEditRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrganizationsEditPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const OrganizationBillingEditPage = loadable(() => import('./features/organizationbilling/add-edit/pages/EditOrganizationBilling'), { resolveComponent: components => components.EditOrganizationBilling, fallback: RouteLoadingFallback });
export const OrganizationBillingEditRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrganizationBillingEditPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const AddStripePaymentMethodPage = loadable(() => import('./features/organizationstripebilling/add-edit/pages/StripeSetupPage'), { resolveComponent: components => components.StripeSetupPage, fallback: RouteLoadingFallback });
export const AddStripePaymentMethodRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <AddStripePaymentMethodPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const StripeReturnPage = loadable(() => import('./features/organizationstripebilling/add-edit/pages/StripeReturnPage'), { resolveComponent: components => components.StripeReturnPage, fallback: RouteLoadingFallback });
export const AddStripeReturnRoute = () => (
    <AnoynmousLayout>
        <StripeReturnPage />
    </AnoynmousLayout>
)

const EnterpriseStripeSetupPage = loadable(() => import('./features/enterpriseStripeBilling/add-edit/pages/EnterpriseStripeSetupPage'), { resolveComponent: components => components.EnterpriseStripeSetupPage, fallback: RouteLoadingFallback });
export const EnterpriseStripeSetupRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <EnterpriseStripeSetupPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const EnterpriseStripeReturnPage = loadable(() => import('./features/enterpriseStripeBilling/add-edit/pages/EnterpriseStripeReturnPage'), { resolveComponent: components => components.EnterpriseStripeReturnPage, fallback: RouteLoadingFallback });
export const EnterpriseStripeReturnRoute = () => (
    <AnoynmousLayout>
        <EnterpriseStripeReturnPage />
    </AnoynmousLayout>
)

const OrgBillingReportsPage = loadable(() => import('./features/organizationbilling/search/pages/OrgBillingReportsSearch'), { resolveComponent: components => components.OrgBillingReportsSearch, fallback: RouteLoadingFallback });
export const OrgBillingReportsRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrgBillingReportsPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const OrgActivityReportsPage = loadable(() => import('./features/organizationbilling/search/pages/OrgActivityReportsSearch'), { resolveComponent: components => components.OrgActivityReportsSearch, fallback: RouteLoadingFallback });
export const OrgActivityReportsRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <OrgActivityReportsPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const SuppliesPage = loadable(() => import('./features/supplies/pages/SuppliesLanding'), { resolveComponent: components => components.SuppliesLanding, fallback: RouteLoadingFallback });
export const SuppliesRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <SuppliesPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const KitsPage = loadable(() => import('./features/kits/pages/KitInventory'), { resolveComponent: components => components.KitInventory, fallback: RouteLoadingFallback });
export const KitsRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <RequireOrderIntent>
                <KitsPage />
            </RequireOrderIntent>
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const KitOrderShipToClinicPage = loadable(() => import('./features/kitOrders/pages/ShipToClinic'), { resolveComponent: components => components.ShipToClinic, fallback: RouteLoadingFallback });
export const KitOrderShipToClinicRoute = () => {
    // location.key changes on every navigation entry, even to the same path - keying on it forces
    // a fresh mount (and re-fires the reset-on-mount effect) each time this route is visited, so a
    // stale "Order Confirmed" screen from a previous visit never lingers when you start a new order.
    const location = useLocation();
    return (
        <ProtectedRoute>
            <AuthenticatedLayout>
                <RequireOrderIntent>
                    <KitOrderShipToClinicPage key={location.key} />
                </RequireOrderIntent>
            </AuthenticatedLayout>
        </ProtectedRoute>
    );
};

const ShipToPatientPage = loadable(() => import('./features/shipToPatient/pages/ShipToPatient'), { resolveComponent: components => components.ShipToPatient, fallback: RouteLoadingFallback });
export const ShipToPatientRoute = () => {
    const location = useLocation();
    return (
        <ProtectedRoute>
            <AuthenticatedLayout>
                <RequireOrderIntent>
                    <ShipToPatientPage key={location.key} />
                </RequireOrderIntent>
            </AuthenticatedLayout>
        </ProtectedRoute>
    );
};

const PatientPaymentPage = loadable(() => import('./features/patientPayment/pages/PatientPayment'), { resolveComponent: components => components.PatientPayment, fallback: RouteLoadingFallback });
export const PatientPaymentRoute = () => (
    <AnoynmousLayout>
        <PatientPaymentPage />
    </AnoynmousLayout>
);

const ClinicSubmissionPage = loadable(() => import('./features/clinicSubmission/pages/ClinicSubmission'), { resolveComponent: components => components.ClinicSubmission, fallback: RouteLoadingFallback });
export const ClinicSubmissionRoute = () => {
    const location = useLocation();
    return (
        <ProtectedRoute>
            <AuthenticatedLayout>
                <RequireOrderIntent>
                    <ClinicSubmissionPage key={location.key} />
                </RequireOrderIntent>
            </AuthenticatedLayout>
        </ProtectedRoute>
    );
};

const SupportPage = loadable(() => import('./features/support/pages/Support'), { resolveComponent: components => components.Support, fallback: RouteLoadingFallback });
export const SupportRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <SupportPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const AccountPage = loadable(() => import('./features/account/pages/Account'), { resolveComponent: components => components.Account, fallback: RouteLoadingFallback });
export const AccountRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <AccountPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const PlatformMaintenancePage = loadable(() => import('./features/platformMaintenance/pages/PlatformMaintenance'), { resolveComponent: components => components.PlatformMaintenance, fallback: RouteLoadingFallback });
export const PlatformMaintenanceRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <PlatformMaintenancePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const AnalyticsDataReferencePage = loadable(() => import('./features/platformMaintenance/pages/AnalyticsDataReference'), { resolveComponent: components => components.AnalyticsDataReference, fallback: RouteLoadingFallback });
// Aug 2026 - the Analytics dashboard's team-facing data reference doc, gated exactly like
// PlatformMaintenanceRoute above (Portal Admin only) rather than published as a public link, since
// it documents internal table/column names.
export const AnalyticsDataReferenceRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <AnalyticsDataReferencePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const RevenueAnalyticsDataReferencePage = loadable(() => import('./features/platformMaintenance/pages/RevenueAnalyticsDataReference'), { resolveComponent: components => components.RevenueAnalyticsDataReference, fallback: RouteLoadingFallback });
// Sep 2026 - Revenue Analytics' own data reference doc, gated exactly like AnalyticsDataReferenceRoute
// above (Portal Admin only) - separate from it now that Revenue Analytics is a fully standalone tool.
export const RevenueAnalyticsDataReferenceRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <RevenueAnalyticsDataReferencePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const TrackingMaintenancePage = loadable(() => import('./features/trackingMaintenance/pages/TrackingMaintenance'), { resolveComponent: components => components.TrackingMaintenance, fallback: RouteLoadingFallback });
// Aug 2026 - not gated like PlatformMaintenanceRoute above - any authenticated user can access
// this, same as Create Order, no Portal Admin check.
export const TrackingMaintenanceRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <TrackingMaintenancePage />
        </AuthenticatedLayout>
    </ProtectedRoute>
);

const SignOutPage = loadable(() => import('./features/auth/pages/SignOut'), { resolveComponent: components => components.SignOut, fallback: RouteLoadingFallback });
export const SignOutRoute = () => (
    <ProtectedRoute>
        <AuthenticatedLayout>
            <SignOutPage />
        </AuthenticatedLayout>
    </ProtectedRoute>
)

const SignOutSimplyPerioPage = loadable(() => import('./features/guidance/perio/pages/SignOut'), { resolveComponent: components => components.SignOutSimplyPerio, fallback: RouteLoadingFallback });
export const SignOutSimplyPerioRoute = () => (
    <AnoynmousLayout>
        <SignOutSimplyPerioPage />
    </AnoynmousLayout>
);

const SignOutSimplyImplantitisPage = loadable(() => import('./features/guidance/implantitis/pages/SignOut'), { resolveComponent: components => components.SignOutSimplyImplantitis, fallback: RouteLoadingFallback });
export const SignOutSimplyImplantitisRoute = () => (
    <AnoynmousLayout>
        <SignOutSimplyImplantitisPage />
    </AnoynmousLayout>
);

